import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Briefcase,
  FileText,
  Search,
  Mail,
  Phone,
  Plus,
  X,
  Archive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PersonnelHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'employees', 'contractors', 'inactive'
  const [newPerson, setNewPerson] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    personnel_type: 'Employee',
    division: '',
    discipline: '',
    status: 'Active',
    send_invitation: false,
    user_role: 'viewer',
  });
  const [stats, setStats] = useState({
    employees: 0,
    contractors: 0,
    applicants: 0,
    jobPostings: 0,
    inactive: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [personnelRes, applicantsRes, postingsRes] = await Promise.all([
        axios.get(`${API}/personnel`),
        axios.get(`${API}/ats/applicants?include_inactive=false`),
        axios.get(`${API}/ats/job-postings?status=Active`),
      ]);

      const personnelData = personnelRes.data;
      // Sort by last name, then first name
      setPersonnel(personnelData.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      }));

      // Count by type (active only) + inactive
      const isActive = (p) => (p.status || '').toLowerCase() === 'active';
      const isContractorType = (t) => t === 'Contractor' || t === '1099';
      const employees = personnelData.filter(p => isActive(p) && p.personnel_type && !isContractorType(p.personnel_type)).length;
      const contractors = personnelData.filter(p => isActive(p) && isContractorType(p.personnel_type)).length;
      const inactive = personnelData.filter(p => !isActive(p)).length;

      setStats({
        employees,
        contractors,
        applicants: applicantsRes.data.length,
        jobPostings: postingsRes.data.length,
        inactive,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPerson = async (e) => {
    e.preventDefault();
    if (!newPerson.first_name || !newPerson.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    if (newPerson.send_invitation && !newPerson.email) {
      toast.error('Email is required to send an invitation');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/personnel`, newPerson);
      if (response.data.invitation_sent) {
        toast.success('Personnel added and invitation email sent!');
      } else if (newPerson.send_invitation) {
        toast.warning('Personnel added but invitation email could not be sent. Check email configuration.');
      } else {
        toast.success('Personnel added successfully');
      }
      setShowAddModal(false);
      setNewPerson({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        personnel_type: 'Employee',
        division: '',
        discipline: '',
        status: 'Active',
        send_invitation: false,
      });
      // Navigate to the new person's detail page
      navigate(`/personnel/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add personnel');
    } finally {
      setSaving(false);
    }
  };

  const filteredPersonnel = personnel.filter((p) => {
    const isActive = (p.status || '').toLowerCase() === 'active';
    const isContractorType = p.personnel_type === 'Contractor' || p.personnel_type === '1099';
    const hasType = !!(p.personnel_type && String(p.personnel_type).trim());

    // Scope filter
    if (activeFilter === 'inactive') {
      if (isActive) return false;
    } else if (activeFilter === 'employees') {
      if (!isActive || !hasType || isContractorType) return false;
    } else if (activeFilter === 'contractors') {
      if (!isActive || !isContractorType) return false;
    } else {
      // 'all' = active staff (Employees, Contractors, Admin, Staff — anyone typed)
      if (!isActive || !hasType) return false;
    }

    // Then apply search filter
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower) ||
      p.phone?.includes(search) ||
      p.discipline?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800';
      case 'On Leave':
        return 'bg-yellow-100 text-yellow-800';
      case 'Terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Employee':
        return 'bg-blue-100 text-blue-800';
      case 'Contractor':
        return 'bg-purple-100 text-purple-800';
      case '1099':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Personnel
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Manage employees, contractors, applicants, and job postings
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              onClick={() => navigate('/personnel/employees?recycle=1')}
              data-testid="hub-recycle-bin-btn"
            >
              <Archive size={16} className="mr-2" />
              Recycle Bin
            </Button>
          )}
          <Button onClick={() => setShowAddModal(true)} data-testid="add-personnel-btn">
            <Plus size={18} className="mr-2" />
            Add Personnel
          </Button>
        </div>
      </div>

      {/* Category Cards - Click to filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sticky top-0 z-10 bg-background py-2">
        <Card
          className={`p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-blue-500 ${
            activeFilter === 'employees' ? 'ring-2 ring-blue-500 bg-blue-50/50' : ''
          }`}
          onClick={() => setActiveFilter(activeFilter === 'employees' ? 'all' : 'employees')}
          data-testid="card-employees"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <div className="text-3xl font-bold">{stats.employees}</div>
              <div className="text-sm text-muted-foreground">Employees</div>
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-purple-500 ${
            activeFilter === 'contractors' ? 'ring-2 ring-purple-500 bg-purple-50/50' : ''
          }`}
          onClick={() => setActiveFilter(activeFilter === 'contractors' ? 'all' : 'contractors')}
          data-testid="card-contractors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <FileText className="text-purple-600" size={24} />
            </div>
            <div>
              <div className="text-3xl font-bold">{stats.contractors}</div>
              <div className="text-sm text-muted-foreground">Contractors</div>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-500"
          onClick={() => navigate('/personnel/applicants')}
          data-testid="card-applicants"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <UserPlus className="text-green-600" size={24} />
            </div>
            <div>
              <div className="text-3xl font-bold">{stats.applicants}</div>
              <div className="text-sm text-muted-foreground">Applicants</div>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
          onClick={() => navigate('/personnel/job-postings')}
          data-testid="card-job-postings"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Briefcase className="text-orange-600" size={24} />
            </div>
            <div>
              <div className="text-3xl font-bold">{stats.jobPostings}</div>
              <div className="text-sm text-muted-foreground">Job Postings</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Personnel List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">
              {activeFilter === 'employees' ? 'Employees' : 
               activeFilter === 'contractors' ? 'Contractors' : 
               activeFilter === 'inactive' ? 'Inactive Personnel' :
               'All Personnel'}
            </h2>
            {activeFilter !== 'all' && (
              <Badge 
                variant="secondary" 
                className="gap-1 cursor-pointer hover:bg-destructive/20"
                onClick={() => setActiveFilter('all')}
              >
                {activeFilter === 'employees' ? 'Employees' : activeFilter === 'contractors' ? 'Contractors' : 'Inactive'}
                <X size={12} />
              </Badge>
            )}
            <span className="text-sm text-muted-foreground">
              ({filteredPersonnel.length})
            </span>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : filteredPersonnel.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto mb-4 text-muted-foreground" size={48} strokeWidth={1.5} />
            <p className="text-muted-foreground">No personnel found</p>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Contact
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Type
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Division
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Discipline
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPersonnel.map((person) => (
                  <tr
                    key={person.id}
                    className="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/personnel/${person.id}`)}
                    data-testid={`personnel-row-${person.id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium">
                        {person.last_name}, {person.first_name}
                      </div>
                      {person.job_title && (
                        <div className="text-xs text-muted-foreground">{person.job_title}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-sm space-y-1">
                        {person.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail size={12} />
                            {person.email}
                          </div>
                        )}
                        {person.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone size={12} />
                            {person.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={getTypeColor(person.personnel_type)}>
                        {person.personnel_type}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{person.division}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{person.discipline || '-'}</span>
                    </td>
                    <td className="p-4">
                      <Badge className={getStatusColor(person.status)}>{person.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Personnel Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Add New Personnel</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleAddPerson} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">First Name *</label>
                  <Input
                    value={newPerson.first_name}
                    onChange={(e) => setNewPerson({ ...newPerson, first_name: e.target.value })}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Name *</label>
                  <Input
                    value={newPerson.last_name}
                    onChange={(e) => setNewPerson({ ...newPerson, last_name: e.target.value })}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <Input
                    type="email"
                    value={newPerson.email}
                    onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Phone</label>
                  <Input
                    value={newPerson.phone}
                    onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPerson.personnel_type}
                    onChange={(e) => setNewPerson({ ...newPerson, personnel_type: e.target.value })}
                  >
                    <option value="Employee">Employee</option>
                    <option value="Contractor">Contractor</option>
                    <option value="1099">1099</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPerson.status}
                    onChange={(e) => setNewPerson({ ...newPerson, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="On Leave">On Leave</option>
                    <option value="Terminated">Terminated</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Division</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPerson.division}
                    onChange={(e) => setNewPerson({ ...newPerson, division: e.target.value })}
                  >
                    <option value="">Select Division</option>
                    <option value="Therapy">Therapy</option>
                    <option value="Administration">Administration</option>
                    <option value="Operations">Operations</option>
                    <option value="Sales">Sales</option>
                    <option value="Finance">Finance</option>
                    <option value="HR">HR</option>
                    <option value="IT">IT</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Discipline</label>
                  <Input
                    value={newPerson.discipline}
                    onChange={(e) => setNewPerson({ ...newPerson, discipline: e.target.value })}
                    placeholder="e.g., PT, OT, SLP, Admin"
                  />
                </div>
              </div>
              
              {/* Send Invitation + Role Checkbox */}
              {newPerson.email && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="send_invitation"
                      checked={newPerson.send_invitation}
                      onChange={(e) => setNewPerson({ ...newPerson, send_invitation: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      data-testid="send-invitation-checkbox"
                    />
                    <div>
                      <label htmlFor="send_invitation" className="text-sm font-medium text-blue-900 cursor-pointer">
                        Create login account &amp; send invitation
                      </label>
                      <p className="text-xs text-blue-700 mt-0.5">
                        They'll get a temporary password by email and can log in.
                      </p>
                    </div>
                  </div>

                  {newPerson.send_invitation && (
                    <div className="ml-7 pl-3 border-l-2 border-blue-300">
                      <label className="text-xs font-medium text-blue-900 mb-1 block">Access level</label>
                      <div className="grid grid-cols-2 gap-2">
                        <label
                          className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${newPerson.user_role === 'viewer' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}
                          data-testid="role-viewer-option"
                        >
                          <input
                            type="radio"
                            name="user_role"
                            value="viewer"
                            checked={newPerson.user_role === 'viewer'}
                            onChange={(e) => setNewPerson({ ...newPerson, user_role: e.target.value })}
                          />
                          <div className="flex-1">
                            <div className="font-medium">User</div>
                            <div className="text-[10px] text-muted-foreground">Standard access</div>
                          </div>
                        </label>
                        <label
                          className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${newPerson.user_role === 'admin' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}
                          data-testid="role-admin-option"
                        >
                          <input
                            type="radio"
                            name="user_role"
                            value="admin"
                            checked={newPerson.user_role === 'admin'}
                            onChange={(e) => setNewPerson({ ...newPerson, user_role: e.target.value })}
                          />
                          <div className="flex-1">
                            <div className="font-medium">Admin</div>
                            <div className="text-[10px] text-muted-foreground">Full access</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Personnel'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
